import { httpsCallable, type HttpsCallable } from 'firebase/functions';
import { z } from 'zod';

import { functions } from '../firebase.js';
import { SchemaValidationFailedError } from '@kilat/shared';

/**
 * Sprint 92 — typed wrappers for the three operator-side subscription
 * callables. The server re-validates with its own Zod; these wrappers
 * give the dashboard fast feedback before a round-trip.
 */

// ── issueWashSubscription ──────────────────────────────────────────────

export const IssueWashSubscriptionInputSchema = z.object({
  tenantId: z.string().min(1),
  outletId: z.string().min(1),
  packageId: z.string().min(1),
  customerUid: z.string().min(1),
  paymentMethod: z.enum([
    'cash',
    'duitnow_qr',
    'tng',
    'grabpay',
    'card',
    'bank_transfer',
    'voucher',
    'free',
  ]),
  paymentRef: z.string().max(120).optional(),
  pricePaidSen: z.number().int().nonnegative().optional(),
});
export type IssueWashSubscriptionInput = z.infer<typeof IssueWashSubscriptionInputSchema>;

export const IssueWashSubscriptionOutputSchema = z.object({
  subscriptionId: z.string(),
  expiresAtMs: z.number().int().positive(),
});
export type IssueWashSubscriptionOutput = z.infer<typeof IssueWashSubscriptionOutputSchema>;

const issueFn: HttpsCallable<IssueWashSubscriptionInput, IssueWashSubscriptionOutput> =
  httpsCallable(functions, 'issueWashSubscription');

export async function callIssueWashSubscription(
  input: IssueWashSubscriptionInput,
): Promise<IssueWashSubscriptionOutput> {
  const parsed = IssueWashSubscriptionInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('issueWashSubscription.input', parsed.error.flatten());
  }
  const result = await issueFn(parsed.data);
  const out = IssueWashSubscriptionOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('issueWashSubscription.output', out.error.flatten());
  }
  return out.data;
}

// ── redeemWashFromSubscription ─────────────────────────────────────────

export const RedeemWashInputSchema = z.object({
  tenantId: z.string().min(1),
  subscriptionId: z.string().min(1),
  ticketId: z.string().min(1),
  serviceId: z.string().min(1),
  outletId: z.string().min(1),
  vehicleTypeId: z.string().optional(),
  notionalValueSen: z.number().int().nonnegative().optional(),
});
export type RedeemWashInput = z.infer<typeof RedeemWashInputSchema>;

export const RedeemWashOutputSchema = z.object({
  ok: z.literal(true),
  remainingAfter: z.number().int().nonnegative(),
  exhausted: z.boolean(),
  usageId: z.string(),
});
export type RedeemWashOutput = z.infer<typeof RedeemWashOutputSchema>;

const redeemFn: HttpsCallable<RedeemWashInput, RedeemWashOutput> = httpsCallable(
  functions,
  'redeemWashFromSubscription',
);

export async function callRedeemWashFromSubscription(
  input: RedeemWashInput,
): Promise<RedeemWashOutput> {
  const parsed = RedeemWashInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError(
      'redeemWashFromSubscription.input',
      parsed.error.flatten(),
    );
  }
  const result = await redeemFn(parsed.data);
  const out = RedeemWashOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('redeemWashFromSubscription.output', out.error.flatten());
  }
  return out.data;
}

// ── cancelWashSubscription ─────────────────────────────────────────────

export const CancelWashSubscriptionInputSchema = z.object({
  tenantId: z.string().min(1),
  subscriptionId: z.string().min(1),
  reason: z.string().max(500).optional(),
});
export type CancelWashSubscriptionInput = z.infer<typeof CancelWashSubscriptionInputSchema>;

export const CancelWashSubscriptionOutputSchema = z.object({ ok: z.literal(true) });
export type CancelWashSubscriptionOutput = z.infer<typeof CancelWashSubscriptionOutputSchema>;

const cancelFn: HttpsCallable<CancelWashSubscriptionInput, CancelWashSubscriptionOutput> =
  httpsCallable(functions, 'cancelWashSubscription');

export async function callCancelWashSubscription(
  input: CancelWashSubscriptionInput,
): Promise<CancelWashSubscriptionOutput> {
  const parsed = CancelWashSubscriptionInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('cancelWashSubscription.input', parsed.error.flatten());
  }
  const result = await cancelFn(parsed.data);
  const out = CancelWashSubscriptionOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('cancelWashSubscription.output', out.error.flatten());
  }
  return out.data;
}
